// VentasTable — las 9 columnas del mock (Fecha · Venta · Cliente · Producto · Tienda · Vend. ·
// Financ. · Monto · Estado) más el estado en La Central, compactas para caber sin scroll horizontal.
import { useTranslation } from 'react-i18next';
import { useCallback, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { formatDateTimeMx, formatShortDateMx, formatTimeMx } from '@/lib/dateHelpers';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import { StatusPill } from '@/shared/patterns/Pills';
import {
    saleProductName,
    SaleTicketProduct,
    ticketHeadline,
    ticketTitle,
} from '@/shared/patterns/SaleTicket';
import {
    SALE_STATUS_DEFAULT_LABEL,
    SALE_STATUS_TONE,
    providerChip,
    financieraLabel,
} from '../lib/ventasFormat';
import { folioTail } from '@/domains/sales/portal/mysales/lib/mySalesFormat';
import { sellerShortName } from '../lib/ventasMetrics';
import { centralCellStatus, centralStatusView } from '../lib/centralStatus';
import { formatStorage } from '@/shared/lib/format/capacity';

/** Las nueve columnas del mock son ordenables; la clave es la columna, no el campo del DTO. */
export type VentasSortKey =
    | 'fecha'
    | 'venta'
    | 'cliente'
    | 'producto'
    | 'tienda'
    | 'vendedor'
    | 'financiera'
    | 'monto'
    | 'estado'
    | 'central';

export interface VentasSort {
    key: VentasSortKey;
    dir: 'asc' | 'desc';
}

/**
 * El orden con el que abre la pantalla: Fecha ▼, el del mock (`sortKey='fecha'`, `sortDir='desc'`).
 * Coincide con el que ya devuelve el endpoint (`sortOrder=desc`), así que aplicarlo no reacomoda
 * nada — por eso la tabla NO declara alcance mientras siga puesto: no hubo reorden en cliente.
 */
export const DEFAULT_SORT: VentasSort = { key: 'fecha', dir: 'desc' };

/**
 * Compara dos celdas dejando los huecos SIEMPRE al final, suban o bajen el resto.
 *
 * Un `null` no es "el valor más chico": es ausencia de dato (el snapshot que la venta no guardó).
 * Si se ordenara como si fuera vacío, invertir la dirección subiría una fila de guiones al tope y
 * el operador leería eso como "las ventas sin cliente son las más antiguas/baratas", que es una
 * afirmación que nadie hizo. Por eso el `null` se resuelve ANTES de aplicar la dirección.
 */
function compareCells(
    a: string | number | null,
    b: string | number | null,
    dir: 'asc' | 'desc',
): number {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    const cmp =
        typeof a === 'number' && typeof b === 'number'
            ? a - b
            : String(a).localeCompare(String(b), 'es-MX');
    return dir === 'asc' ? cmp : -cmp;
}

/**
 * Ordena las ventas YA CARGADAS. No pide nada al backend a propósito: el endpoint no acepta un
 * parámetro de orden (DDB entrega por la range key del GSI), así que el único orden posible del lado
 * del front es sobre lo que ya trajo. Devuelve una copia — `sales` es la data de react-query y
 * mutarla in situ rompería su cache.
 *
 * `financiera` se recibe como función y no se lee del DTO porque esa columna muestra una etiqueta
 * derivada ("Contado" cuando no hay proveedor): se ordena por lo que el operador VE, no por el campo
 * crudo. Sin eso, las ventas de contado se irían al final como si les faltara el dato.
 */
export function sortSales(
    sales: BackofficeSale[],
    sort: VentasSort | null | undefined,
    financiera: (s: BackofficeSale) => string | null,
): BackofficeSale[] {
    if (!sort) return sales;

    const cellOf = (s: BackofficeSale): string | number | null => {
        switch (sort.key) {
            case 'fecha':
                // ISO-8601 UTC: el orden lexicográfico ES el cronológico, no hace falta parsear.
                return s.soldAt;
            case 'venta':
                return s.folio;
            case 'cliente':
                return saleCustomerLabel(s);
            case 'producto':
                return ticketHeadline(s, null, saleProductName(s)).name;
            case 'tienda':
                return s.storeName;
            case 'vendedor':
                return s.sellerName;
            case 'financiera':
                return financiera(s);
            case 'monto':
                // Centavos y el MISMO valor que pinta la celda (lo vendido).
                return soldCentsOf(s);
            case 'estado':
                return s.status;
            case 'central':
                return centralStatusView(centralCellStatus(s)).key;
        }
    };

    // `sort` de JS es estable desde ES2019: las filas empatadas conservan el orden del backend.
    return [...sales].sort((a, b) => compareCells(cellOf(a), cellOf(b), sort.dir));
}

/** La financiera que ve el operador, con los rótulos del locale. La usan la tabla y su orden. */
export function useProviderLabelOf(): (s: BackofficeSale) => string | null {
    const { t } = useTranslation('ventas');
    return useCallback(
        (s: BackofficeSale) =>
            financieraLabel(s, {
                surekeep: t('tabla.surekeep', { defaultValue: 'SureKeep' }),
                contado: t('tabla.contado', { defaultValue: 'Contado' }),
            }),
        [t],
    );
}

export interface VentasTableProps {
    /** Filas ya ordenadas y de la página vigente. `null` = no hay lista: se pinta `unavailable`. */
    sales: BackofficeSale[] | null;
    /** Qué decir en lugar de la tabla cuando `sales` es `null`. */
    unavailable?: ReactNode;
    /** Primera página en camino: filas de esqueleto. */
    isLoading?: boolean;
    /** Cuántas ventas entraron al orden; por defecto, las filas recibidas. */
    sortedCount?: number;
    /** Venta abierta en la columna de detalle; se resalta en la lista. */
    selectedId: string | null;
    onSelect: (saleId: string) => void;
    /** Orden vigente; `null`/ausente = el orden con el que llegó del backend. */
    sort?: VentasSort | null;
    /** Ausente ⇒ encabezados NO clicables (la tabla se usa como lista pasiva). */
    onSortChange?: (next: VentasSort) => void;
    /** `true` mientras queden páginas por traer: endurece el aviso de alcance del orden. */
    hasMore?: boolean;
    className?: string;
}

/** Celda de un campo del snapshot: `null` se pinta `—`, nunca vacío ni con otra fuente. */
function snapshotCell(value: string | null): ReactNode {
    return value ?? <span className="text-muted-foreground">—</span>;
}

/** Marca + modelo + capacidad para el tooltip; sin ellas, el nombre del equipo o su SKU. */
function productFullName(s: BackofficeSale): string {
    const capacity = formatStorage(s.productCapacity);
    const name = saleProductName(s);
    return capacity && (s.productBrand || s.productModel) ? `${name} ${capacity}` : name;
}

export function VentasTable({
    sales,
    selectedId,
    onSelect,
    sort,
    onSortChange,
    hasMore = false,
    unavailable,
    isLoading = false,
    sortedCount,
    className,
}: VentasTableProps) {
    const { t } = useTranslation('ventas');
    const providerLabelOf = useProviderLabelOf();
    const col = {
        fecha: t('tabla.fecha', { defaultValue: 'Fecha' }),
        venta: t('tabla.venta', { defaultValue: 'Venta' }),
        cliente: t('tabla.cliente', { defaultValue: 'Cliente' }),
        producto: t('tabla.producto', { defaultValue: 'Producto' }),
        tienda: t('tabla.tienda', { defaultValue: 'Tienda' }),
        vendedor: t('tabla.vendedor', { defaultValue: 'Vend.' }),
        financiera: t('tabla.financiera', { defaultValue: 'Financ.' }),
        monto: t('tabla.monto', { defaultValue: 'Monto' }),
        estado: t('tabla.estado', { defaultValue: 'Estado' }),
        central: t('tabla.central', { defaultValue: 'Central' }),
    };
    const centralLabel = (status: string | null | undefined): string => {
        const view = centralStatusView(status);
        return t(`central.estado.${view.key}`, { defaultValue: view.defaultLabel });
    };

    const today = formatShortDateMx(new Date().toISOString());
    const columns: DataTableColumn<BackofficeSale, VentasSortKey>[] = [
        {
            key: 'fecha',
            label: col.fecha,
            width: '75px',
            sortKey: 'fecha',
            cellOverflow: 'nowrap',
            cellTitle: (s) => formatDateTimeMx(s.soldAt),
            cellClassName: () => 'text-[10.5px] tabular-nums',
            cell: (s) => {
                const day = formatShortDateMx(s.soldAt);
                return (
                    <>
                        <span className={day === today ? 'font-semibold' : undefined}>{day}</span>{' '}
                        <span className="text-muted-foreground">{formatTimeMx(s.soldAt)}</span>
                    </>
                );
            },
        },
        {
            key: 'venta',
            label: col.venta,
            width: '84px',
            sortKey: 'venta',
            cellTitle: (s) => s.folio,
            cellClassName: () => 'font-mono text-[9.5px]',
            cell: (s) => folioTail(s.folio),
        },
        {
            key: 'cliente',
            label: col.cliente,
            sortKey: 'cliente',
            cellTitle: (s) => saleCustomerLabel(s) ?? undefined,
            cellClassName: () => 'font-medium',
            cell: (s) => snapshotCell(saleCustomerLabel(s)),
        },
        {
            key: 'producto',
            label: col.producto,
            width: '80px',
            sortKey: 'producto',
            cellTitle: (s) => ticketTitle(s, productFullName(s)),
            cell: (s) => (
                <SaleTicketProduct sale={s} category={null} saleName={saleProductName(s)} />
            ),
        },
        {
            key: 'tienda',
            label: col.tienda,
            width: '64px',
            sortKey: 'tienda',
            cellTitle: (s) => s.storeName ?? undefined,
            cellClassName: () => 'text-[10.5px]',
            cell: (s) => snapshotCell(s.storeName),
        },
        {
            key: 'vendedor',
            label: col.vendedor,
            width: '50px',
            sortKey: 'vendedor',
            cellTitle: (s) => s.sellerName ?? undefined,
            cellClassName: () => 'text-[10.5px] text-muted-foreground',
            cell: (s) => snapshotCell(sellerShortName(s.sellerName)),
        },
        {
            key: 'financiera',
            label: col.financiera,
            width: '40px',
            sortKey: 'financiera',
            cellOverflow: 'nowrap',
            cellTitle: (s) => providerLabelOf(s) ?? undefined,
            cell: (s) => {
                const chip = providerChip(s);
                return chip === null ? (
                    snapshotCell(null)
                ) : (
                    <StatusPill tone={chip.tone}>{chip.short}</StatusPill>
                );
            },
        },
        {
            key: 'monto',
            label: col.monto,
            width: '74px',
            numeric: true,
            sortKey: 'monto',
            cellOverflow: 'nowrap',
            cellTitle: (s) => formatMXN(soldCentsOf(s) / 100),
            cellClassName: () => 'tabular-nums',
            cell: (s) => formatMXN(soldCentsOf(s) / 100),
        },
        {
            key: 'estado',
            label: col.estado,
            width: '72px',
            sortKey: 'estado',
            cellOverflow: 'nowrap',
            cell: (s) => (
                <StatusPill tone={SALE_STATUS_TONE[s.status] ?? 'neutral'}>
                    {t(`estado.${s.status}`, {
                        defaultValue: SALE_STATUS_DEFAULT_LABEL[s.status] ?? s.status,
                    })}
                </StatusPill>
            ),
        },
        {
            key: 'central',
            label: col.central,
            width: '72px',
            sortKey: 'central',
            cellOverflow: 'nowrap',
            cellTitle: (s) =>
                `${t('central.registro', { defaultValue: 'Registro' })}: ${centralLabel(s.centralSyncStatus)} · ${t('central.cancelacion', { defaultValue: 'Cancelación' })}: ${centralLabel(s.centralCancelStatus)}`,
            cell: (s) => {
                const status = centralCellStatus(s);
                return (
                    <StatusPill tone={centralStatusView(status).tone}>
                        {centralLabel(status)}
                    </StatusPill>
                );
            },
        },
    ];

    const n = sortedCount ?? sales?.length ?? 0;
    // El aviso de alcance solo tiene sentido cuando el operador REORDENÓ el orden del backend.
    const reordenado = !!sort && !(sort.key === DEFAULT_SORT.key && sort.dir === DEFAULT_SORT.dir);

    return (
        <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
            <DataTable
                columns={columns}
                rows={sales}
                isLoading={isLoading}
                unavailable={unavailable}
                rowKey={(s) => s.saleId}
                density="compact"
                caption={t('tabla.caption', {
                    defaultValue:
                        'Ventas de la cadena con su folio, cliente, producto, tienda, vendedor, financiera, monto, estado y estado en La Central.',
                })}
                sort={sort ? { key: sort.key, direction: sort.dir } : undefined}
                onSortToggle={
                    onSortChange
                        ? (key) => {
                              // Primer clic en una columna nueva: ascendente. Reclic sobre la activa: alterna.
                              const dir = sort?.key === key && sort.dir === 'asc' ? 'desc' : 'asc';
                              onSortChange({ key, dir });
                          }
                        : undefined
                }
                onRowClick={(s) => {
                    onSelect(s.saleId);
                }}
                isRowSelected={(s) => s.saleId === selectedId}
                empty={
                    // Con páginas pendientes no se puede afirmar que no haya coincidencias más adelante.
                    hasMore
                        ? t('tabla.vacioParcial', {
                              defaultValue:
                                  'Sin coincidencias en lo cargado hasta ahora. Trae más páginas para seguir buscando.',
                          })
                        : t('tabla.vacio', {
                              defaultValue: 'Ninguna venta coincide con los filtros.',
                          })
                }
            />
            {reordenado && sales !== null ? (
                <p className="px-2 py-1 text-center text-[10.5px] italic text-muted-foreground">
                    {hasMore
                        ? t('tabla.ordenAlcanceParcial', {
                              n,
                              defaultValue:
                                  'Orden aplicado sobre las {{n}} ventas cargadas — faltan páginas por traer',
                          })
                        : t('tabla.ordenAlcance', {
                              n,
                              defaultValue: 'Orden aplicado sobre las {{n}} ventas cargadas',
                          })}
                </p>
            ) : null}
        </div>
    );
}
